import { useRef } from 'react';
import { CanvasElement } from './PhoneCanvas';

interface PropertiesPanelProps {
  element: CanvasElement | null;
  appSettings: { primaryColor: string; backgroundColor: string; fontFamily: string };
  onUpdateElement: (id: string, props: Record<string, unknown>) => void;
  onDeleteElement: (id: string) => void;
  onUpdateSettings: (settings: Partial<{ primaryColor: string; backgroundColor: string; fontFamily: string }>) => void;
}

const colorThemes = [
  { name: 'Teal', primary: '#14B8A6', bg: '#FFFFFF' },
  { name: 'Blue', primary: '#3B82F6', bg: '#FFFFFF' },
  { name: 'Purple', primary: '#8B5CF6', bg: '#FFFFFF' },
  { name: 'Pink', primary: '#EC4899', bg: '#FFFFFF' },
  { name: 'Orange', primary: '#F97316', bg: '#FFFFFF' },
  { name: 'Green', primary: '#10B981', bg: '#FFFFFF' },
  { name: 'Red', primary: '#EF4444', bg: '#FFFFFF' },
  { name: 'Indigo', primary: '#6366F1', bg: '#FFFFFF' },
  { name: 'Dark', primary: '#14B8A6', bg: '#111827' },
  { name: 'Light Gray', primary: '#14B8A6', bg: '#F9FAFB' },
];

const fontOptions = [
  'Inter',
  'Roboto',
  'Poppins',
  'Montserrat',
  'Open Sans',
  'Lato',
  'Raleway',
  'Nunito',
];

export default function PropertiesPanel({ element, appSettings, onUpdateElement, onDeleteElement, onUpdateSettings }: PropertiesPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!element) {
    return (
      <div className="p-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Color Theme</label>
          <div className="grid grid-cols-2 gap-2">
            {colorThemes.map((theme) => (
              <button
                key={theme.name}
                onClick={() => onUpdateSettings({ primaryColor: theme.primary, backgroundColor: theme.bg })}
                className={`p-2 rounded-lg border-2 transition-all ${
                  appSettings.primaryColor === theme.primary && appSettings.backgroundColor === theme.bg
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: theme.primary }}></div>
                  <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: theme.bg }}></div>
                </div>
                <span className="text-xs font-medium text-gray-700">{theme.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Font Family</label>
          <select
            value={appSettings.fontFamily}
            onChange={(e) => onUpdateSettings({ fontFamily: e.target.value })}
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none bg-white"
          >
            {fontOptions.map((font) => (
              <option key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Primary Color</label>
          <input
            type="color"
            value={appSettings.primaryColor}
            onChange={(e) => onUpdateSettings({ primaryColor: e.target.value })}
            className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Background Color</label>
          <input
            type="color"
            value={appSettings.backgroundColor}
            onChange={(e) => onUpdateSettings({ backgroundColor: e.target.value })}
            className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
          />
        </div>
      </div>
    );
  }

  const p = element.properties;
  const update = (key: string, value: unknown) => onUpdateElement(element.id, { [key]: value });

  return (
    <div className="p-4 space-y-4">
      {/* Common properties based on element type */}
      {element.type === 'button' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Button Text</label>
            <input
              type="text"
              value={p.text as string}
              onChange={(e) => update('text', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Color</label>
            <input
              type="color"
              value={p.backgroundColor as string}
              onChange={(e) => update('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Text Color</label>
            <input
              type="color"
              value={p.textColor as string}
              onChange={(e) => update('textColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Border Radius</label>
            <input
              type="range"
              min="0"
              max="24"
              value={p.borderRadius as number}
              onChange={(e) => update('borderRadius', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.borderRadius}px</span>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.fullWidth as boolean}
                onChange={(e) => update('fullWidth', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Full Width</span>
            </label>
          </div>
        </>
      )}

      {(element.type === 'text' || element.type === 'heading') && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Content</label>
            <textarea
              value={p.content as string}
              onChange={(e) => update('content', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Font Size</label>
            <input
              type="range"
              min="10"
              max="48"
              value={p.fontSize as number}
              onChange={(e) => update('fontSize', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.fontSize}px</span>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Color</label>
            <input
              type="color"
              value={p.color as string}
              onChange={(e) => update('color', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          {element.type === 'text' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Font Weight</label>
              <select
                value={p.fontWeight as string}
                onChange={(e) => update('fontWeight', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
              >
                <option value="normal">Normal</option>
                <option value="semibold">Semibold</option>
                <option value="bold">Bold</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Alignment</label>
            <div className="flex gap-1">
              {['left', 'center', 'right'].map((align) => (
                <button
                  key={align}
                  onClick={() => update('align', align)}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${
                    p.align === align ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {align.charAt(0).toUpperCase() + align.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Image */}
      {element.type === 'image' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Image URL</label>
            <input
              type="text"
              value={p.src as string}
              onChange={(e) => update('src', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Alt Text</label>
            <input
              type="text"
              value={p.alt as string}
              onChange={(e) => update('alt', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Height</label>
            <input
              type="range"
              min="60"
              max="400"
              value={p.height as number}
              onChange={(e) => update('height', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.height}px</span>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Border Radius</label>
            <input
              type="range"
              min="0"
              max="32"
              value={p.borderRadius as number}
              onChange={(e) => update('borderRadius', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.borderRadius}px</span>
          </div>
        </>
      )}

      {/* Card */}
      {element.type === 'card' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Subtitle</label>
            <input
              type="text"
              value={p.subtitle as string}
              onChange={(e) => update('subtitle', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Color</label>
            <input
              type="color"
              value={p.backgroundColor as string}
              onChange={(e) => update('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.shadow as boolean}
                onChange={(e) => update('shadow', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Show Shadow</span>
            </label>
          </div>
        </>
      )}

      {/* Input */}
      {element.type === 'input' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Label</label>
            <input
              type="text"
              value={p.label as string}
              onChange={(e) => update('label', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Placeholder</label>
            <input
              type="text"
              value={p.placeholder as string}
              onChange={(e) => update('placeholder', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Type</label>
            <select
              value={p.type as string}
              onChange={(e) => update('type', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            >
              {['text', 'email', 'password', 'number', 'tel'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </>
      )}

      {/* Header */}
      {element.type === 'header' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Color</label>
            <input
              type="color"
              value={p.backgroundColor as string}
              onChange={(e) => update('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.showBack as boolean}
                onChange={(e) => update('showBack', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Show Back Button</span>
            </label>
          </div>
        </>
      )}

      {/* Progress */}
      {element.type === 'progress' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Value</label>
            <input
              type="range"
              min="0"
              max="100"
              value={p.value as number}
              onChange={(e) => update('value', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.value}%</span>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Color</label>
            <input
              type="color"
              value={p.color as string}
              onChange={(e) => update('color', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Height</label>
            <input
              type="range"
              min="4"
              max="24"
              value={p.height as number}
              onChange={(e) => update('height', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.height}px</span>
          </div>
        </>
      )}

      {/* Toggle */}
      {element.type === 'toggle' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Label</label>
            <input
              type="text"
              value={p.label as string}
              onChange={(e) => update('label', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Color</label>
            <input
              type="color"
              value={p.color as string}
              onChange={(e) => update('color', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.enabled as boolean}
                onChange={(e) => update('enabled', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Enabled by Default</span>
            </label>
          </div>
        </>
      )}

      {/* List Item */}
      {element.type === 'list' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Subtitle</label>
            <input
              type="text"
              value={p.subtitle as string}
              onChange={(e) => update('subtitle', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.showArrow as boolean}
                onChange={(e) => update('showArrow', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Show Arrow</span>
            </label>
          </div>
        </>
      )}

      {/* Product Card */}
      {element.type === 'productcard' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Product Name</label>
            <input
              type="text"
              value={p.name as string}
              onChange={(e) => update('name', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Price</label>
            <input
              type="text"
              value={p.price as string}
              onChange={(e) => update('price', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Original Price</label>
            <input
              type="text"
              value={p.originalPrice as string}
              onChange={(e) => update('originalPrice', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Badge</label>
            <input
              type="text"
              value={p.badge as string}
              onChange={(e) => update('badge', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
        </>
      )}

      {/* Spacer */}
      {element.type === 'spacer' && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Height</label>
          <input
            type="range"
            min="4"
            max="120"
            value={p.height as number}
            onChange={(e) => update('height', parseInt(e.target.value))}
            className="w-full"
          />
          <span className="text-xs text-gray-500">{p.height}px</span>
        </div>
      )}

      {/* Avatar */}
      {element.type === 'avatar' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Initials</label>
            <input
              type="text"
              value={p.initials as string}
              onChange={(e) => update('initials', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Color</label>
            <input
              type="color"
              value={p.backgroundColor as string}
              onChange={(e) => update('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Size</label>
            <input
              type="range"
              min="32"
              max="96"
              value={p.size as number}
              onChange={(e) => update('size', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.size}px</span>
          </div>
        </>
      )}

      {/* Badge */}
      {element.type === 'badge' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Text</label>
            <input
              type="text"
              value={p.text as string}
              onChange={(e) => update('text', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Color</label>
            <input
              type="color"
              value={p.backgroundColor as string}
              onChange={(e) => update('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Text Color</label>
            <input
              type="color"
              value={p.textColor as string}
              onChange={(e) => update('textColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
        </>
      )}

      {/* Search */}
      {element.type === 'search' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Placeholder</label>
            <input
              type="text"
              value={p.placeholder as string}
              onChange={(e) => update('placeholder', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Background Color</label>
            <input
              type="color"
              value={p.backgroundColor as string}
              onChange={(e) => update('backgroundColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
        </>
      )}

      {/* Notification */}
      {element.type === 'notification' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Body</label>
            <input
              type="text"
              value={p.body as string}
              onChange={(e) => update('body', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Time</label>
            <input
              type="text"
              value={p.time as string}
              onChange={(e) => update('time', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.unread as boolean}
                onChange={(e) => update('unread', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Mark as Unread</span>
            </label>
          </div>
        </>
      )}

      {/* User Card */}
      {element.type === 'usercard' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Name</label>
            <input
              type="text"
              value={p.name as string}
              onChange={(e) => update('name', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Role</label>
            <input
              type="text"
              value={p.role as string}
              onChange={(e) => update('role', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Followers</label>
            <input
              type="text"
              value={p.followers as string}
              onChange={(e) => update('followers', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
        </>
      )}

      {element.type === 'countdown' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Target Date</label>
            <input
              type="date"
              value={p.targetDate as string}
              onChange={(e) => update('targetDate', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Color</label>
            <input
              type="color"
              value={p.color as string}
              onChange={(e) => update('color', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={p.showDays as boolean} onChange={(e) => update('showDays', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
              <span className="text-xs font-semibold text-gray-700">Show Days</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={p.showHours as boolean} onChange={(e) => update('showHours', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
              <span className="text-xs font-semibold text-gray-700">Show Hours</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={p.showMinutes as boolean} onChange={(e) => update('showMinutes', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
              <span className="text-xs font-semibold text-gray-700">Show Minutes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={p.showSeconds as boolean} onChange={(e) => update('showSeconds', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
              <span className="text-xs font-semibold text-gray-700">Show Seconds</span>
            </label>
          </div>
        </>
      )}

      {element.type === 'notificationbanner' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Message</label>
            <textarea
              value={p.message as string}
              onChange={(e) => update('message', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Type</label>
            <select
              value={p.type as string}
              onChange={(e) => update('type', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={p.dismissible as boolean} onChange={(e) => update('dismissible', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
              <span className="text-xs font-semibold text-gray-700">Dismissible</span>
            </label>
          </div>
        </>
      )}

      {element.type === 'qrcode' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Data/URL</label>
            <input
              type="text"
              value={p.data as string}
              onChange={(e) => update('data', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Size</label>
            <input
              type="range"
              min="80"
              max="200"
              value={p.size as number}
              onChange={(e) => update('size', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.size}px</span>
          </div>
        </>
      )}

      {element.type === 'chart' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Color</label>
            <input
              type="color"
              value={p.color as string}
              onChange={(e) => update('color', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
        </>
      )}

      {(element.type === 'loginform' || element.type === 'signupform' || element.type === 'paymentform') && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Button Color</label>
            <input
              type="color"
              value={p.buttonColor as string}
              onChange={(e) => update('buttonColor', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          {(element.type === 'loginform' || element.type === 'signupform') && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={p.showSocial as boolean} onChange={(e) => update('showSocial', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
                <span className="text-xs font-semibold text-gray-700">Show Social Login</span>
              </label>
            </div>
          )}
          {element.type === 'paymentform' && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={p.showSavedCards as boolean} onChange={(e) => update('showSavedCards', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
                <span className="text-xs font-semibold text-gray-700">Show Saved Cards</span>
              </label>
            </div>
          )}
        </>
      )}

      {element.type === 'onboarding' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Number of Slides</label>
            <input
              type="number"
              min="2"
              max="5"
              value={p.slides as number}
              onChange={(e) => update('slides', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Color</label>
            <input
              type="color"
              value={p.color as string}
              onChange={(e) => update('color', e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={p.showSkip as boolean} onChange={(e) => update('showSkip', e.target.checked)} className="w-4 h-4 text-teal-500 rounded" />
              <span className="text-xs font-semibold text-gray-700">Show Skip Button</span>
            </label>
          </div>
        </>
      )}

      {element.type === 'socialshare' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={p.title as string}
              onChange={(e) => update('title', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Platforms</label>
            <div className="space-y-2">
              {['facebook', 'twitter', 'instagram', 'whatsapp'].map((platform) => (
                <label key={platform} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(p.platforms as string[]).includes(platform)}
                    onChange={(e) => {
                      const current = p.platforms as string[];
                      const updated = e.target.checked
                        ? [...current, platform]
                        : current.filter((pl) => pl !== platform);
                      update('platforms', updated);
                    }}
                    className="w-4 h-4 text-teal-500 rounded"
                  />
                  <span className="text-xs font-semibold text-gray-700 capitalize">{platform}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Image Upload Component */}
      {element.type === 'imageupload' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Label</label>
            <input
              type="text"
              value={p.label as string}
              onChange={(e) => update('label', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Hint Text</label>
            <input
              type="text"
              value={p.hint as string}
              onChange={(e) => update('hint', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none"
            />
          </div>

          {/* Upload Image Button */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Upload Image</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={p.allowMultiple as boolean}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  update('uploadedSrc', ev.target?.result as string);
                };
                reader.readAsDataURL(file);
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-2.5 border-2 border-dashed border-teal-300 bg-teal-50 hover:bg-teal-100 text-teal-700 font-semibold text-xs rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-upload-cloud-2-line text-base"></i>
              Choose Image from Device
            </button>
            {p.uploadedSrc && (
              <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={p.uploadedSrc as string}
                  alt="Preview"
                  className="w-full h-24 object-cover"
                />
                <button
                  onClick={() => update('uploadedSrc', '')}
                  className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer shadow"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-xs text-center py-1 font-medium">
                  Image uploaded ✓
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Height</label>
            <input
              type="range"
              min="80"
              max="300"
              value={p.height as number}
              onChange={(e) => update('height', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.height}px</span>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Border Radius</label>
            <input
              type="range"
              min="0"
              max="32"
              value={p.borderRadius as number}
              onChange={(e) => update('borderRadius', parseInt(e.target.value))}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{p.borderRadius}px</span>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={p.allowMultiple as boolean}
                onChange={(e) => update('allowMultiple', e.target.checked)}
                className="w-4 h-4 text-teal-500 rounded"
              />
              <span className="text-xs font-semibold text-gray-700">Allow Multiple Images</span>
            </label>
          </div>
        </>
      )}

      <button
        onClick={() => onDeleteElement(element.id)}
        className="w-full py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-xs rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer"
      >
        <i className="ri-delete-bin-line"></i>
        Delete Element
      </button>
    </div>
  );
}
